import './monthly-update.css';
import StartupPageTitle from '../../components/StartupPageTitle';
import { Calendar as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useState, useEffect, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { DownloadSimple, Upload, CheckCircle, FileXls, SpinnerGap, FilePdf, CaretDown, ArrowRight, ChartBar, NotePencil } from '@phosphor-icons/react';

const MonthlyUpdateForm = ({ user, onSuccess }) => {
  const sectionRefs = useRef([]);
  const [stagedFile, setStagedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const openSection = (index, toggle = false) => setActiveStep(previous => toggle && previous === index ? -1 : index);
  const fileInputRef = useRef(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [existingUpdate, setExistingUpdate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [savingQualitative, setSavingQualitative] = useState(false);
  const [allUpdates, setAllUpdates] = useState([]);

  const [activeStep, setActiveStep] = useState(0);
  const [notesSaved, setNotesSaved] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [qualData, setQualData] = useState({
    win_this_month: '',
    challenge_this_month: '',
    goals_next_month: '',
    help_needed: '',
    trl: '',
    new_products: '',
    new_campaigns: '',
    campaign_links: '',
    new_categories: ''
  });

  useEffect(() => {
    checkExistingUpdate();
  }, [user?.id]);

  useEffect(() => {
    if (activeStep < 0) return;
    const frame = requestAnimationFrame(() => sectionRefs.current[activeStep]?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'}));
    return () => cancelAnimationFrame(frame);
  }, [activeStep]);

  const checkExistingUpdate = async () => {
    try {
      const res = await api.get('/startup/monthly-updates');
      const updates = res.data || [];
      setAllUpdates(updates);
      const thisMonth = updates.find(u => Number(u.month) === currentMonth && Number(u.year) === currentYear);
      setExistingUpdate(thisMonth || null);
      // Preview uploads never create records. A persisted current-month record
      // represents a submitted update and survives logout/page reload.
      setSubmitted(Boolean(thisMonth));
      setNotesSaved(Boolean(thisMonth));
      if (thisMonth) {
        setExistingUpdate(thisMonth);
        setQualData({
          win_this_month: thisMonth.win_this_month || '',
          challenge_this_month: thisMonth.challenge_this_month || '',
          goals_next_month: thisMonth.goals_next_month || '',
          help_needed: thisMonth.help_needed || '',
          trl: thisMonth.trl || '',
          new_products: thisMonth.new_products || '',
          new_campaigns: thisMonth.new_campaigns || '',
          campaign_links: thisMonth.campaign_links || '',
          new_categories: thisMonth.new_categories || ''
        });
      }
    } catch (err) {
      console.error('Failed to check existing updates', err);
    } finally {
      setCheckingExisting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadBusy(true);
    try {
      const res = await api.get('/startup/mis/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'Founderboard_MIS.xlsx';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('MIS template downloaded!');
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch { /* Non-JSON server error */ }
      }
      console.error('MIS template download failed', err);
      toast.error(typeof detail === 'string' ? detail : `Template download failed${err.response?.status ? ` (${err.response.status})` : ''}. Please check the backend is running.`);
    } finally { setDownloadBusy(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Please upload an Excel file (.xlsx)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { toast.error('Please choose a file smaller than 10MB'); return; }
    setUploading(true);
    setUploadResult(null);
    setStagedFile(null);
    setUploadProgress(0);
    const startedAt = performance.now();
    // Presentation progress eases between milestones and waits at 98% for validation.
    const milestones = [[0, 0], [650, 12], [1800, 38], [2700, 48], [3800, 73], [4700, 82], [5700, 96], [6000, 98]];
    const progressTimer = window.setInterval(() => {
      const elapsed = performance.now() - startedAt;
      let progress = 98;
      for (let i = 1; i < milestones.length; i++) {
        const [endTime, endValue] = milestones[i];
        if (elapsed <= endTime) {
          const [startTime, startValue] = milestones[i - 1];
          const fraction = (elapsed - startTime) / (endTime - startTime);
          progress = startValue + (endValue - startValue) * (1 - Math.pow(1 - fraction, 2));
          break;
        }
      }
      setUploadProgress(Math.floor(progress));
    }, 80);
    try {
      const fd = new FormData();
      fd.append('file', file);
      setUploadProgress(0);
      const res = await api.post('/startup/mis/upload?preview=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const remaining = Math.max(0, 6000 - (performance.now() - startedAt));
      if (remaining > 0) await new Promise(resolve => window.setTimeout(resolve, remaining));
      window.clearInterval(progressTimer);
      setUploadResult(res.data);
      setStagedFile(file);
      setUploadProgress(100);
      setSubmitted(false);
      toast.success('Workbook checked. Submit in section 3 to save your update.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      window.clearInterval(progressTimer);
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (savingQualitative || uploading) return;
    if (!uploadResult?.current_month && !existingUpdate) { toast.error('Add current month data before submitting'); return; }
    setSavingQualitative(true);
    try {
      if (stagedFile) {
        const fd = new FormData(); fd.append('file', stagedFile); fd.append('qualitative', JSON.stringify(qualData));
        await api.post('/startup/mis/upload', fd);
      } else {
        await api.post('/startup/monthly-update/qualitative', {month: currentMonth, year: currentYear, ...qualData});
      }
      setSubmitted(true); setNotesSaved(true); setStagedFile(null);
      await checkExistingUpdate(); onSuccess?.();
      toast.success('Monthly update submitted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Submission failed. Your draft is still here; please retry.'); }
    finally { setSavingQualitative(false); }
  };

  const handleDownloadSnapshot = async (month, year) => {
    try {
      const params = month && year ? `?month=${month}&year=${year}` : '';
      const res = await api.get(`/startup/snapshot/pdf${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'Monthly_Snapshot.pdf';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Snapshot PDF downloaded!');
    } catch (err) {
      toast.error('Failed to generate snapshot');
    }
  };

  const monthLabel = new Date(currentYear, currentMonth - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  const reviewMetrics = uploadResult?.current_month || existingUpdate;
  const canContinue = Boolean(reviewMetrics) && !uploading;
  const steps = ['MIS & quick wins', 'Performance context', 'Review & snapshot'];
  const fields = [
    ['goals_next_month', 'Goals for next month', 'What are your top priorities?'],
    ['new_products', 'New products introduced', 'Product launches or new features'],
    ['new_campaigns', 'New campaigns started', 'Marketing campaigns, partnerships or events'],
    ['campaign_links', 'Campaign links', 'https://…'],
    ['new_categories', 'New categories identified', 'Markets, segments or opportunities'],
    ['help_needed', 'Help needed from investors', 'Introductions, advice or resources'],
  ];
  const field = (key, label, placeholder) => (
    <label className="mu-field" key={key}>
      <span>{label}</span>
      <textarea value={qualData[key]} onChange={e => { setQualData(p => ({ ...p, [key]: e.target.value })); setNotesSaved(false); setSubmitted(false); }} rows={key === 'campaign_links' ? 1 : 3} placeholder={placeholder} />
    </label>
  );
  const sections = [
    ['Quick wins & roadblocks', 'Upload your monthly numbers and share what moved the needle.'],
    ['Performance context', 'Add the context behind your numbers. These notes are optional.'],
    ['Review & snapshot', 'Review this month and download a snapshot for your investors.'],
  ];
  if (checkingExisting) return <div className="mu-page" role="status"><SpinnerGap className="animate-spin" size={24} /> Loading monthly updates…</div>;
  return (
    <div className="mu-page">
      <header className="startup-page-header">
        <div className="mu-heading-row">
          <StartupPageTitle icon={PageHeadingIcon}>{monthLabel} Update</StartupPageTitle>
          <span className={`mu-status ${submitted ? 'is-ready' : stagedFile ? 'is-draft' : 'is-pending'}`}>{submitted ? 'Submitted' : stagedFile ? 'Draft ready' : 'Pending'}</span>
        </div>
        <p>Keep your investors aligned with monthly numbers, progress and plans.</p>
      </header>
      <div className="mu-layout">
        <div className="mu-sections">
          {sections.map(([title, description], index) => (
            <section ref={el => { sectionRefs.current[index] = el; }} key={title} className={`mu-section ${activeStep === index ? 'is-open' : ''}`}>
              <button className="mu-section-toggle" onClick={() => openSection(index, true)} aria-expanded={activeStep === index} aria-controls={`mu-section-${index}`}>
                <span className="mu-section-number">{index === 0 && existingUpdate || index === 1 && notesSaved ? <CheckCircle size={20} weight="fill" /> : String(index + 1).padStart(2, '0')}</span>
                <span>{title}</span><span className="mu-section-meta">{index === 0 ? 'Monthly data' : index === 1 ? 'Optional notes' : 'Summary'}</span><CaretDown size={16} />
              </button>
              {activeStep === index && <div className="mu-section-body" id={`mu-section-${index}`}>
                <p className="mu-description">{description}</p>
                {index === 0 && <>
                  <div className="mu-template">
                    <FileXls size={28} className="mu-teal" /><div><strong>Your monthly MIS workbook</strong><p>Every month from your founding year to today. Existing data is prefilled; missing months are ready to complete.</p></div>
                    <button className="mu-button mu-outline" disabled={downloadBusy} onClick={handleDownloadTemplate} data-testid="download-template-btn"><DownloadSimple size={16} />{downloadBusy ? 'Preparing…' : 'Download template'}</button>
                  </div>
                  <div className={`mu-upload ${uploading ? 'is-busy' : ''}`}>
                    {uploading ? <SpinnerGap size={28} className="animate-spin mu-teal" /> : uploadResult ? <CheckCircle size={28} className="mu-teal" /> : <Upload size={28} className="mu-teal" />}
                    <strong>{uploading ? uploadProgress >= 82 ? 'Finishing validation…' : uploadProgress >= 48 ? 'Checking monthly numbers…' : 'Reading your workbook…' : uploadResult ? 'Workbook ready for review' : 'Upload your completed MIS'}</strong>
                    <span>{stagedFile?.name || 'Excel workbook · .xlsx · up to 10MB'}</span>
                    {(uploading || uploadResult) && <div className="mu-upload-progress" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100} aria-label="Workbook validation"><div style={{width: `${uploadProgress}%`}} /></div>}
                    {uploadResult && !uploading && <span>{uploadResult.months_processed} months ready · {uploadResult.months_skipped} empty months skipped · Saved on final submit</span>}
                    <div className="mu-upload-ctas"><button className="mu-button mu-outline" disabled={uploading || savingQualitative} onClick={() => fileInputRef.current?.click()}>{uploadResult ? 'Replace workbook' : 'Choose workbook'}</button></div>
                    <input type="file" className="mu-hidden-file" accept=".xlsx" onChange={handleUpload} ref={fileInputRef} disabled={uploading || savingQualitative} data-testid="mis-upload-input" aria-label="Upload completed MIS workbook" />
                  </div>
                  <div className="mu-fields mu-wins">{field('win_this_month', 'Biggest win this month', 'What went exceptionally well?')}{field('challenge_this_month', 'Biggest challenge this month', 'Where could your investors help?')}</div>
                  <div className="mu-actions"><button className="mu-button mu-primary" onClick={() => openSection(1)} disabled={!canContinue}>Continue <ArrowRight size={16} /></button></div>
                  {!reviewMetrics && <p className="mu-hint">Upload data for the current month to continue. You can also fill missing historical months in the same workbook.</p>}
                </>}
                {index === 1 && <>
                  <label className="mu-field mu-stage"><span>Technology readiness level</span><select value={qualData.trl} onChange={e => { setQualData(p => ({ ...p, trl: e.target.value })); setNotesSaved(false); setSubmitted(false); }}><option value="">Select stage</option>{['Proof of Concept', 'Prototype', 'Pilot', 'Commercial'].map(stage => <option key={stage}>{stage}</option>)}</select></label>
                  <div className="mu-fields">{fields.map(args => field(...args))}</div>
                  <div className="mu-actions"><button className="mu-button mu-outline" onClick={() => openSection(0)}>Back</button><button className="mu-button mu-primary" disabled={!canContinue} onClick={() => { setNotesSaved(true); openSection(2); }}>Continue<ArrowRight size={16} /></button></div>
                </>}
                {index === 2 && <>
                  <div className="mu-review"><ChartBar size={26} className="mu-teal" /><div><strong>{monthLabel}</strong><p>{reviewMetrics ? `${uploadResult?.months_processed || 1} month(s) ready · ${submitted ? 'Submitted' : 'Awaiting final submit'}` : 'Current month data is missing'}</p></div></div>
                  <div className="mu-kpis">{[['Revenue', 'revenue_total', true], ['Cash in bank', 'cash_in_bank', true], ['Net burn', 'net_burn', true], ['Team size', 'headcount', false]].map(([label,key,money]) => <div key={key}><span>{label}</span><strong>{reviewMetrics?.[key] == null ? '—' : new Intl.NumberFormat('en-IN', {notation:'compact', maximumFractionDigits:1, ...(money ? {style:'currency',currency:'INR'} : {})}).format(reviewMetrics[key])}</strong><small>{monthLabel}</small></div>)}</div>
                  <div className="mu-summary-heading"><h3>Monthly highlights</h3></div>
                  <dl className="mu-highlight-summary">{[['Biggest win', qualData.win_this_month], ['Biggest challenge', qualData.challenge_this_month]].map(([label,value], index) => <div key={label} className={index === 0 ? 'mu-highlight-win' : 'mu-highlight-challenge'}><dt>{label}</dt><dd className={!value ? 'is-empty' : ''}>{value || 'No highlight added'}</dd></div>)}</dl>
                  <div className="mu-summary-heading"><h3>Performance context</h3>{qualData.trl && <span className="mu-readiness">{qualData.trl}</span>}</div>
                  <dl className="mu-context-summary">{fields.map(([key,label]) => <div key={key} className={!qualData[key] ? 'is-empty' : ''}><dt>{label}</dt><dd>{qualData[key] || 'Not added'}</dd></div>)}</dl>

                  <div className="mu-actions"><button className="mu-button mu-outline" onClick={() => openSection(1)}>Back</button>{submitted ? <button className="mu-button mu-primary" onClick={() => handleDownloadSnapshot(currentMonth, currentYear)}><FilePdf size={18} />Download snapshot</button> : <button className="mu-button mu-primary" disabled={!canContinue || savingQualitative} onClick={handleSubmit}>{savingQualitative ? 'Submitting…' : 'Submit update'}<CheckCircle size={18} /></button>}</div>
                </>}
              </div>}
            </section>
          ))}
          <section className="mu-history">
            <div className="mu-history-heading"><h2>Previous updates</h2><span>{allUpdates.length} recorded months</span></div>
            <p className="mu-description">Add missing historical months using the MIS workbook above.</p>
            {allUpdates.length ? [...allUpdates].sort((a,b) => b.year - a.year || b.month - a.month).map(update => <div className="mu-history-row" key={`${update.year}-${update.month}`}><span>{new Date(update.year, update.month - 1).toLocaleString('en-IN', {month:'long',year:'numeric'})}</span><button className="mu-button mu-outline" onClick={() => handleDownloadSnapshot(update.month, update.year)}><FilePdf size={16} />Snapshot</button></div>) : <p className="mu-hint">Your uploaded months will appear here.</p>}
          </section>
        </div>
        <aside className="mu-progress" aria-label="Monthly update progress">
          <span className="mu-eyebrow">MONTHLY MIS & QUALITATIVE</span><h2>{monthLabel}</h2>
          <ol>{steps.map((step,index) => <li key={step} className={activeStep === index ? 'is-active' : ''}><button onClick={() => openSection(index, true)} aria-current={activeStep === index ? 'step' : undefined}><span className="mu-step-dot">{index === 0 && existingUpdate || index === 1 && notesSaved ? <CheckCircle size={18} /> : index + 1}</span><span><strong>{step}</strong><small>{index === 0 && existingUpdate ? 'Data available' : index === 1 && notesSaved ? 'Ready for review' : activeStep === index ? 'In progress' : 'Open section'}</small></span></button></li>)}</ol>
          <div className="mu-progress-note"><NotePencil size={22} /><p>One workbook, your full history.<br /><span>Leave unavailable months blank.</span></p></div>
          <p className="mu-hint">Your workbook and notes stay as a draft until you submit in section 3.</p>
        </aside>
      </div>
    </div>
  );
};
export default MonthlyUpdateForm;
